import { useState } from 'react'
import { openDocument } from '../api/documents.js'

/**
 * "Open" control for a clinical document.
 *
 * Fetches the file through the authenticated axios client as a blob and
 * opens it as an object URL — NOT a plain <a href>. Clinical-document
 * binaries are served only from a JWT-gated endpoint
 * (GET /api/documents/<id>/download/); the raw /media/clinical-documents/*
 * path is blocked for PHI, and a bare browser navigation / new tab cannot
 * send the bearer token, so a plain link 404s. See api/documents.js.
 *
 * Renders nothing when the document has no id.
 *
 * @param {object}  props
 * @param {object}  props.doc        ClinicalDocument row (needs `id`)
 * @param {string} [props.label]     button text (default "Open")
 * @param {string} [props.className] override styling
 */
export default function DocumentLink({ doc, label = 'Open', className = '' }) {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  if (!doc?.id) return null

  async function handleOpen() {
    setBusy(true)
    setFailed(false)
    try {
      await openDocument(doc)
    } catch {
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleOpen}
      disabled={busy}
      title={failed ? 'Could not open the document — please try again.' : undefined}
      className={
        className
        || 'text-xs text-wantok-700 underline shrink-0 disabled:opacity-50'
      }
    >
      {busy ? 'Opening…' : failed ? 'Retry' : label}
    </button>
  )
}
